import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { MarketplaceListing, MarketplaceRate } from '../../models/apiTypes';
import ModalFrame from '../primitives/ModalFrame';
import { cn } from '../cn';


export interface SellHouseModalProps {
  showSellModal: boolean;
  setShowSellModal: Dispatch<SetStateAction<boolean>>;
  t: TFunction;
  listingLoading: boolean;
  activeListing: MarketplaceListing | null;
  sellRate: MarketplaceRate | null;
  sellLoading: boolean;
  handleCancelListing: () => Promise<void>;
  showSellTooltip: boolean;
  setShowSellTooltip: Dispatch<SetStateAction<boolean>>;
  sellEditingField: 'ton' | 'usdt';
  sellPriceTon: string;
  handleSellTonChange: (val: string) => void;
  setSellEditingField: Dispatch<SetStateAction<'ton' | 'usdt'>>;
  sellPriceUsdt: string;
  handleSellUsdtChange: (val: string) => void;
  handleSellQuickUsdt: (addUsdt: number) => void;
  handleListForSale: () => Promise<void>;
}

export default function SellHouseModal({
  showSellModal,
  setShowSellModal,
  t,
  listingLoading,
  activeListing,
  sellRate,
  sellLoading,
  handleCancelListing,
  showSellTooltip,
  setShowSellTooltip,
  sellEditingField,
  sellPriceTon,
  handleSellTonChange,
  setSellEditingField,
  sellPriceUsdt,
  handleSellUsdtChange,
  handleSellQuickUsdt,
  handleListForSale,
}: SellHouseModalProps) {
  const listDisabled = !sellPriceUsdt || !sellPriceTon || sellLoading;
  return (
    <ModalFrame
      isOpen={showSellModal}
      onClose={() => setShowSellModal(false)}
      title={t("Sell House")}
      variant="dialog"
      className="max-w-[352px] rounded-xl"
      backdropClassName="modal-frame-bottom-gradient [--modal-frame-gradient-max-width:352px]"
      showBackButton={true}
    >
      <div className="px-xl pb-xl [font-family:Lato,sans-serif]">
        {listingLoading ? (
          <div className="p-4xl text-center text-[16px] text-text-subtle">
            {t("Loading...")}
          </div>
        ) : activeListing ? (
          /* Active listing exists */
          <div>
            <div className="mb-xl py-[14px] text-center">
              <div className="mb-sm text-[12px] tracking-[0.5px] text-text-subtle uppercase">
                {t("Listed for sale")}
              </div>
              <div className="flex items-baseline justify-center gap-md">
                <span className="text-[24px] font-bold text-white">
                  ${activeListing.price_usdt.toFixed(2)}
                </span>
                <span className="text-[14px] text-ton-label">
                  ≈ {parseFloat(activeListing.price_ton.toFixed(4))} TON
                </span>
              </div>
            </div>
            <div className="mb-xl rounded-[10px] bg-white-alpha-5 p-lg text-[13px] break-all text-text-subtle">
              <div className="mb-xs">MEMO: <span className="text-white">{activeListing.memo}</span></div>
              <div>Wallet: <span className="text-ton-label">{sellRate?.wallet_address || '—'}</span></div>
            </div>
            <div className="mb-xl text-[13px] leading-[18px] text-text-subtle">
              {t("Buyer sends TON with this MEMO to the wallet above. House transfers automatically after payment.")}
            </div>
            <button
              onClick={handleCancelListing}
              disabled={sellLoading}
              className="w-full cursor-pointer rounded-md border border-danger-soft-alpha-40 bg-danger-soft-alpha-10 p-[14px] text-[16px] font-semibold text-error-soft"
            >
              {sellLoading ? t("Cancelling...") : t("Cancel listing")}
            </button>
          </div>
        ) : (
          /* Create new listing — TON on top, USDT below, both editable */
          <div>
            {/* Info tooltip */}
            <div className="relative mb-lg flex items-center justify-center gap-sm">
              <span className="[font-family:Lato,sans-serif] text-[13px] text-white-alpha-50">
                {t("Set the price in TON or USDT — the other field updates automatically.")}
              </span>
              <span
                className="inline-flex h-xl w-xl shrink-0 cursor-pointer items-center justify-center rounded-full border border-white-alpha-40 text-[10px] text-white-alpha-60"
                onClick={(e) => { e.stopPropagation(); setShowSellTooltip(v => !v); }}
              >
                ?
              </span>
              {showSellTooltip && (
                <>
                  <div
                    className="fixed inset-0 z-[998]"
                    onClick={() => setShowSellTooltip(false)}
                  />
                  <div
                    className="absolute top-3xl right-0 left-0 z-[999] rounded-sm border border-ton-action-alpha-40 bg-popover-surface-alpha-97 px-lg py-[10px] [font-family:Lato,sans-serif] text-[12px] leading-[1.5] text-white"
                    onClick={() => setShowSellTooltip(false)}
                  >
                    {t("Your house will be listed on the marketplace. Buyers send TON with a unique MEMO to the project wallet. Once the payment is confirmed, the house transfers automatically. The exchange rate is updated in real time.")}
                  </div>
                </>
              )}
            </div>

            {/* TON input */}
            <div className="mb-sm text-[13px] font-semibold text-ton-label">TON</div>
            <div
              className={cn(
                'mb-lg flex items-center rounded-md px-xl',
                sellEditingField === 'ton'
                  ? 'border border-ton-action-alpha-30 bg-ton-action-alpha-10'
                  : 'border border-transparent bg-white-alpha-6',
              )}
            >
              <span className="mr-md text-[18px]">💎</span>
              <input
                type="text"
                inputMode="decimal"
                value={sellPriceTon}
                onChange={(e) => handleSellTonChange(e.target.value)}
                onFocus={() => setSellEditingField('ton')}
                placeholder="0.0000"
                className="flex-1 border-none bg-transparent py-lg [font-family:Lato,sans-serif] text-[22px] font-bold text-white outline-none"
              />
            </div>

            {/* Sync indicator */}
            <div className="my-xs text-center text-[16px] text-text-dim">⇅</div>

            {/* USDT input */}
            <div className="mb-sm text-[13px] font-semibold text-usdt-label">USDT</div>
            <div
              className={cn(
                'mb-md flex items-center rounded-md px-xl',
                sellEditingField === 'usdt'
                  ? 'border border-usdt-label-alpha-30 bg-usdt-label-alpha-10'
                  : 'border border-transparent bg-white-alpha-6',
              )}
            >
              <span className="mr-md text-[18px] text-usdt-label">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={sellPriceUsdt}
                onChange={(e) => handleSellUsdtChange(e.target.value)}
                onFocus={() => setSellEditingField('usdt')}
                placeholder="0.00"
                className="flex-1 border-none bg-transparent py-lg [font-family:Lato,sans-serif] text-[22px] font-bold text-white outline-none"
              />
            </div>

            {/* Quick price buttons */}
            <div className="mb-lg flex gap-md">
              {[5, 10, 25].map((qa) => (
                <button
                  key={qa}
                  onClick={() => handleSellQuickUsdt(qa)}
                  className="flex-1 cursor-pointer rounded-[10px] border border-white-alpha-12 bg-white-alpha-5 py-md text-[14px] font-semibold text-usdt-label"
                >
                  +${qa}
                </button>
              ))}
            </div>

            {sellRate && (
              <div className="mb-xl text-center text-[12px] text-text-dim">
                1 TON ≈ ${sellRate.ton_usdt.toFixed(2)} USDT
              </div>
            )}

            <button
              onClick={handleListForSale}
              disabled={listDisabled}
              className={cn(
                'w-full rounded-md border-none p-[14px] text-[16px] font-semibold text-white',
                listDisabled
                  ? 'cursor-default bg-white-alpha-10 opacity-50'
                  : 'cursor-pointer bg-[image:var(--gradient-ton-action)] opacity-100',
              )}
            >
              {sellLoading ? t("Listing...") : t("List for sale")}
            </button>
          </div>
        )}
      </div>
    </ModalFrame>
  );
}
